package com.mps.asc_app.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Switch
import androidx.compose.material3.Tab
import androidx.compose.material3.TabRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.mps.asc_app.data.places.model.AddressSuggestion
import com.mps.asc_app.data.user.MemberProfile
import com.mps.asc_app.data.user.MemberSummary
import com.mps.asc_app.data.user.TrainingGroup
import com.mps.asc_app.data.user.TrainingGroupType
import com.mps.asc_app.data.user.UserRepository
import com.mps.asc_app.data.user.UserStatus
import com.mps.asc_app.data.functions.FunctionsRepository
import com.mps.asc_app.config.DebugRegistrationConfig
import com.mps.asc_app.data.config.ConfigRepository
import com.mps.asc_app.domain.places.usecase.AutocompleteAddressUseCase
import com.mps.asc_app.presentation.AdminPanelUiState
import com.mps.asc_app.presentation.AdminPanelViewModel
import com.mps.asc_app.util.exportCsv
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.koin.compose.koinInject

private val SPORTS = listOf("Padel", "Tennis", "Fußball", "Dart", "Badminton", "Ohne Sparte")
private val SPORTS_FOR_EDIT = listOf("Padel", "Tennis", "Fußball", "Dart", "Badminton")
private const val NO_SPORT_FILTER_KEY = "ohnesparte"

private enum class PrivacyConsentFilter {
    ALL,
    ACCEPTED,
    NOT_ACCEPTED,
}

private enum class AdminLayoutTab(val label: String) {
    OVERVIEW("Übersicht"),
    MEMBERS("Nutzer"),
    FILTERS("Filter"),
    CONFIG("Konfig"),
}

private fun normalizeSportKey(value: String): String {
    return value
        .trim()
        .lowercase()
        .replace("ß", "ss")
        .replace("ä", "ae")
        .replace("ö", "oe")
        .replace("ü", "ue")
        .replace(" ", "")
}

private fun toCanonicalSportLabel(raw: String): String? {
    val key = normalizeSportKey(raw)
    return when (key) {
        "padel" -> "Padel"
        "tennis" -> "Tennis"
        "fussball" -> "Fußball" // deckt auch "Fussball" und "Fußball" ab (ß->ss)
        "dart" -> "Dart"
        "badminton" -> "Badminton"
        else -> null
    }
}

@Composable
fun AdminPanelScreen(
    contentPadding: PaddingValues = PaddingValues(),
    onBack: () -> Unit,
) {
    val viewModel: AdminPanelViewModel = koinInject()
    val uiState by viewModel.state.collectAsState()

    val userRepository: UserRepository = koinInject()
    val functionsRepository: FunctionsRepository = koinInject()
    val configRepository: ConfigRepository = koinInject()
    val autocompleteAddressUseCase: AutocompleteAddressUseCase = koinInject()
    val scope = rememberCoroutineScope()

    var panelMessage by rememberSaveable { mutableStateOf<String?>(null) }
    var selectedTab by rememberSaveable { mutableStateOf(AdminLayoutTab.OVERVIEW) }

    val clipboardManager = LocalClipboardManager.current

    val exportEmails = remember(
        uiState.admins,
        uiState.trainers,
        uiState.approved,
        uiState.pending,
        uiState.emailNotVerified,
    ) {
        val allMembers = (uiState.admins + uiState.trainers + uiState.approved + uiState.pending + uiState.emailNotVerified)
            .distinctBy { it.uid }
        val tennisAndPadelMembers = allMembers.filter {
            memberBelongsToTennisGroup(it) || memberBelongsToPadelGroup(it)
        }
        (uiState.admins + uiState.trainers + tennisAndPadelMembers)
            .mapNotNull { it.email.trim().takeIf { email -> email.isNotBlank() } }
            .distinctBy { it.lowercase() }
            .sortedBy { it.lowercase() }
    }
    val exportCsvText = remember(exportEmails) {
        exportEmails.joinToString(separator = "\n", postfix = if (exportEmails.isNotEmpty()) "\n" else "")
    }
    var exportFeedback by rememberSaveable { mutableStateOf<String?>(null) }

    // --- User löschen (aus Liste, z.B. E-Mail nicht bestätigt) ---
    var deleteRowUid by rememberSaveable { mutableStateOf<String?>(null) }
    var deleteRowLoading by remember { mutableStateOf(false) }
    var deleteRowError by rememberSaveable { mutableStateOf<String?>(null) }

    // --- Member bearbeiten (Dialog) ---
    var editUid by rememberSaveable { mutableStateOf<String?>(null) }
    var editProfile by remember { mutableStateOf<MemberProfile?>(null) }
    var editLoading by remember { mutableStateOf(false) }
    var editError by rememberSaveable { mutableStateOf<String?>(null) }

    var deleteLoading by remember { mutableStateOf(false) }
    var deleteError by rememberSaveable { mutableStateOf<String?>(null) }

    val openEditor: (MemberSummary) -> Unit = remember(userRepository) {
        { member ->
            panelMessage = null
            scope.launch {
                // Sicherheitscheck: Editor nur für bestätigte E-Mails (Admins ausgenommen).
                if (!member.isAdmin) {
                    val flagResult = functionsRepository.getEmailVerifiedFlags(listOf(member.uid))
                    val isVerified = flagResult.getOrNull()?.get(member.uid) == true
                    if (!isVerified) {
                        panelMessage = "Dieser Nutzer hat seine E-Mail-Adresse noch nicht bestätigt."
                        return@launch
                    }
                }

                editUid = member.uid
                editProfile = null
                editError = null
                editLoading = true
                deleteLoading = false
                deleteError = null

                runCatching { userRepository.getMemberProfile(member.uid) }
                    .onSuccess { editProfile = it }
                    .onFailure { e ->
                        editError = e.message ?: "Profil konnte nicht geladen werden."
                    }
                editLoading = false
            }
        }
    }

    var firstSlot by rememberSaveable { mutableStateOf("10:00") }
    var lastSlot by rememberSaveable { mutableStateOf("22:00") }
    var intervalMinutes by rememberSaveable { mutableStateOf("60") }
    var bookingEnabled by rememberSaveable { mutableStateOf(true) }
    var bookingNamesPublic by rememberSaveable { mutableStateOf(true) }
    var bookingNamesFeedback by rememberSaveable { mutableStateOf<String?>(null) }
    var bookingOpenAt by rememberSaveable { mutableStateOf("2025-09-19 10:00") }
    var searchQuery by rememberSaveable { mutableStateOf("") }
    var sortLabel by rememberSaveable { mutableStateOf("Neueste zuerst") }
    var hideMembersWithId by rememberSaveable { mutableStateOf(false) }
    var onlyWithIdWhenSportFilter by rememberSaveable { mutableStateOf(false) }
    var selectedDateFilter by rememberSaveable { mutableStateOf("") }
    var sortByRegistrationDate by rememberSaveable { mutableStateOf(false) }
    var trainingGroups by remember { mutableStateOf<List<TrainingGroup>>(emptyList()) }
    var newGroupName by rememberSaveable { mutableStateOf("") }
    var newGroupType by rememberSaveable { mutableStateOf(TrainingGroupType.TEAM) }
    var trainingGroupFeedback by rememberSaveable { mutableStateOf<String?>(null) }
    var backfillLoading by remember { mutableStateOf(false) }
    var backfillFeedback by rememberSaveable { mutableStateOf<String?>(null) }
    var statusCleanupLoading by remember { mutableStateOf(false) }
    var statusCleanupFeedback by rememberSaveable { mutableStateOf<String?>(null) }

    // Config initial laden (inkl. Namenssichtbarkeit)
    LaunchedEffect(Unit) {
        runCatching { configRepository.getTimeslotConfig() }
            .onSuccess { cfg ->
                bookingEnabled = cfg.bookingEnabled
                bookingNamesPublic = cfg.bookingNamesPublic
                firstSlot = cfg.start
                lastSlot = cfg.end
                intervalMinutes = cfg.intervalMinutes.toString()
            }
            .onFailure { e ->
                bookingNamesFeedback = e.message ?: "Konfiguration konnte nicht geladen werden."
            }

        runCatching { userRepository.getTrainingGroups() }
            .onSuccess { groups ->
                trainingGroups = groups
            }
            .onFailure { e ->
                trainingGroupFeedback = e.message ?: "Gruppen konnten nicht geladen werden."
            }
    }

    var selectedSports by remember { mutableStateOf(setOf<String>()) }
    var privacyConsentFilter by rememberSaveable { mutableStateOf(PrivacyConsentFilter.ALL) }
    val filteredAdmins = remember(
        uiState.admins,
        searchQuery,
        privacyConsentFilter,
        selectedSports,
        hideMembersWithId,
        onlyWithIdWhenSportFilter,
    ) {
        applySportAndMemberNumberFilters(
            members = applyPrivacyConsentFilter(applySearch(uiState.admins, searchQuery), privacyConsentFilter),
            selectedSports = selectedSports,
            hideMembersWithId = hideMembersWithId,
            onlyWithIdWhenSportFilter = onlyWithIdWhenSportFilter,
        )
    }
    val filteredTrainers = remember(
        uiState.trainers,
        searchQuery,
        privacyConsentFilter,
        selectedSports,
        hideMembersWithId,
        onlyWithIdWhenSportFilter,
    ) {
        applySportAndMemberNumberFilters(
            members = applyPrivacyConsentFilter(applySearch(uiState.trainers, searchQuery), privacyConsentFilter),
            selectedSports = selectedSports,
            hideMembersWithId = hideMembersWithId,
            onlyWithIdWhenSportFilter = onlyWithIdWhenSportFilter,
        )
    }
    val filteredApproved = remember(
        uiState.approved,
        searchQuery,
        privacyConsentFilter,
        selectedSports,
        hideMembersWithId,
        onlyWithIdWhenSportFilter,
    ) {
        applySportAndMemberNumberFilters(
            members = applyPrivacyConsentFilter(applySearch(uiState.approved, searchQuery), privacyConsentFilter),
            selectedSports = selectedSports,
            hideMembersWithId = hideMembersWithId,
            onlyWithIdWhenSportFilter = onlyWithIdWhenSportFilter,
        )
    }
    val filteredPending = remember(
        uiState.pending,
        searchQuery,
        privacyConsentFilter,
        selectedSports,
        hideMembersWithId,
        onlyWithIdWhenSportFilter,
    ) {
        applySportAndMemberNumberFilters(
            members = applyPrivacyConsentFilter(applySearch(uiState.pending, searchQuery), privacyConsentFilter),
            selectedSports = selectedSports,
            hideMembersWithId = hideMembersWithId,
            onlyWithIdWhenSportFilter = onlyWithIdWhenSportFilter,
        )
    }
    val filteredEmailNotVerified = remember(
        uiState.emailNotVerified,
        searchQuery,
        privacyConsentFilter,
        selectedSports,
        hideMembersWithId,
        onlyWithIdWhenSportFilter,
    ) {
        applySportAndMemberNumberFilters(
            members = applyPrivacyConsentFilter(applySearch(uiState.emailNotVerified, searchQuery), privacyConsentFilter),
            selectedSports = selectedSports,
            hideMembersWithId = hideMembersWithId,
            onlyWithIdWhenSportFilter = onlyWithIdWhenSportFilter,
        )
    }
    val sortNewestFirst = sortLabel == "Neueste zuerst"
    val displayedAdmins = remember(filteredAdmins, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(filteredAdmins, sortByRegistrationDate, sortNewestFirst)
    }
    val displayedTrainers = remember(filteredTrainers, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(filteredTrainers, sortByRegistrationDate, sortNewestFirst)
    }
    val displayedApproved = remember(filteredApproved, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(filteredApproved, sortByRegistrationDate, sortNewestFirst)
    }
    val displayedPending = remember(filteredPending, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(filteredPending, sortByRegistrationDate, sortNewestFirst)
    }
    val displayedEmailNotVerified = remember(filteredEmailNotVerified, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(filteredEmailNotVerified, sortByRegistrationDate, sortNewestFirst)
    }
    val displayedAllMembersForSports = remember(
        displayedAdmins,
        displayedTrainers,
        displayedApproved,
        displayedPending,
        displayedEmailNotVerified,
    ) {
        (displayedAdmins + displayedTrainers + displayedApproved + displayedPending + displayedEmailNotVerified)
            .distinctBy { it.uid }
    }
    val displayedPadelMembers = remember(displayedAllMembersForSports, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(
            members = displayedAllMembersForSports.filter {
                memberHasSport(it, "Padel") && !memberHasSport(it, "Tennis")
            },
            sortByRegistrationDate = sortByRegistrationDate,
            newestFirst = sortNewestFirst,
        )
    }
    val displayedTennisMembers = remember(displayedAllMembersForSports, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(
            members = displayedAllMembersForSports.filter(::memberBelongsToTennisGroup),
            sortByRegistrationDate = sortByRegistrationDate,
            newestFirst = sortNewestFirst,
        )
    }
    val displayedMembersWithoutSport = remember(displayedAllMembersForSports, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(
            members = displayedAllMembersForSports.filter { member ->
                !member.isAdmin &&
                    !member.isTrainer &&
                    !memberHasAnySport(member) &&
                    member.memberNumber.trim().isNotBlank()
            },
            sortByRegistrationDate = sortByRegistrationDate,
            newestFirst = sortNewestFirst,
        )
    }
    val displayedNoMemberMembers = remember(displayedAllMembersForSports, sortByRegistrationDate, sortNewestFirst) {
        sortMembersForDisplay(
            members = displayedAllMembersForSports.filter { member ->
                !member.isAdmin &&
                    !member.isTrainer &&
                    !memberHasAnySport(member) &&
                    member.memberNumber.trim().isBlank()
            },
            sortByRegistrationDate = sortByRegistrationDate,
            newestFirst = sortNewestFirst,
        )
    }
    val allMembersForOverview = remember(
        uiState.admins,
        uiState.trainers,
        uiState.approved,
        uiState.pending,
        uiState.emailNotVerified,
    ) {
        (uiState.admins + uiState.trainers + uiState.approved + uiState.pending + uiState.emailNotVerified)
            .distinctBy { it.uid }
    }
    val padelCount = remember(allMembersForOverview) {
        allMembersForOverview.count(::memberBelongsToPadelGroup)
    }
    val tennisCount = remember(allMembersForOverview) {
        allMembersForOverview.count(::memberBelongsToTennisGroup)
    }
    val showOverviewTab = selectedTab == AdminLayoutTab.OVERVIEW
    val showMembersTab = selectedTab == AdminLayoutTab.MEMBERS
    val showFiltersTab = selectedTab == AdminLayoutTab.FILTERS
    val showConfigTab = selectedTab == AdminLayoutTab.CONFIG
    val openCount = remember(filteredEmailNotVerified) {
        filteredEmailNotVerified.size
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background)
            .padding(contentPadding)
            .padding(16.dp)
    ) {
        if (editUid != null) {
            MemberEditDialog(
                profile = editProfile,
                isLoading = editLoading,
                loadError = editError,
                isDeleting = deleteLoading,
                deleteError = deleteError,
                functionsRepository = functionsRepository,
                availableTrainingGroups = trainingGroups,
                autocompleteAddressUseCase = autocompleteAddressUseCase,
                onDismiss = {
                    editUid = null
                    editProfile = null
                    editError = null
                    editLoading = false
                    deleteLoading = false
                    deleteError = null
                },
                onSave = { updated ->
                    scope.launch {
                        runCatching {
                            userRepository.updateMemberProfile(
                                uid = updated.uid,
                                firstName = updated.firstName,
                                lastName = updated.lastName,
                                memberNumber = updated.memberNumber,
                                phone = updated.phone,
                                addressText = updated.addressText,
                                placeId = updated.placeId,
                                sports = updated.sports,
                                isAdmin = updated.isAdmin,
                                isTrainer = updated.isTrainer,
                                trainerAssignments = updated.trainerAssignments,
                                manuallyChecked = updated.manuallyChecked,
                            )
                        }.onSuccess {
                            viewModel.refresh()
                            trainingGroups = runCatching { userRepository.getTrainingGroups() }.getOrDefault(trainingGroups)
                            editUid = null
                            editProfile = null
                            editError = null
                            editLoading = false
                        }.onFailure { e ->
                            editError = e.message ?: "Speichern fehlgeschlagen."
                        }
                    }
                },
                onDelete = { uidToDelete ->
                    scope.launch {
                        deleteError = null
                        deleteLoading = true

                        // Selbstschutz: Admin sollte sich nicht selbst löschen.
                        val currentUid = Firebase.auth.currentUser?.uid
                        if (!currentUid.isNullOrBlank() && currentUid == uidToDelete) {
                            deleteError = "Du kannst dich nicht selbst löschen."
                            deleteLoading = false
                            return@launch
                        }

                        val result = functionsRepository.adminDeleteUser(uidToDelete)
                        result.onSuccess {
                            viewModel.refresh()
                            editUid = null
                            editProfile = null
                            editError = null
                            editLoading = false
                        }.onFailure { e ->
                            deleteError = e.message ?: "Löschen fehlgeschlagen."
                        }

                        deleteLoading = false
                    }
                }
            )
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.background)
        ) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(
                    text = "Admin",
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = MaterialTheme.colorScheme.primary,
                )

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Box(
                        modifier = Modifier
                            .border(
                                width = 1.dp,
                                color = MaterialTheme.colorScheme.outlineVariant,
                                shape = RoundedCornerShape(12.dp)
                            )
                            .background(
                                color = MaterialTheme.colorScheme.surface,
                                shape = RoundedCornerShape(12.dp)
                            )
                            .padding(horizontal = 12.dp, vertical = 8.dp)
                    ) {
                        Text(
                            text = "Offen: $openCount",
                            color = MaterialTheme.colorScheme.onSurface
                        )
                    }
                    // "Zurück" entfällt: die Navigation unten führt zurück zur Buchung (Redesign Stufe 2).
                }
            }
        }

        Spacer(Modifier.height(12.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.background)
        ) {
            TabRow(
                selectedTabIndex = selectedTab.ordinal,
                containerColor = MaterialTheme.colorScheme.background,
                contentColor = MaterialTheme.colorScheme.onBackground,
            ) {
                AdminLayoutTab.entries.forEach { tab ->
                    Tab(
                        selected = selectedTab == tab,
                        onClick = { selectedTab = tab },
                        text = { Text(tab.label) }
                    )
                }
            }
        }

        if (showOverviewTab) {
            Spacer(Modifier.height(12.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OverviewCard(
                    title = "Admins",
                    value = uiState.admins.size.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                    valueColor = MaterialTheme.colorScheme.onPrimaryContainer,
                )
                OverviewCard(
                    title = "Trainer",
                    value = uiState.trainers.size.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.tertiaryContainer,
                    valueColor = MaterialTheme.colorScheme.onTertiaryContainer,
                )
                OverviewCard(
                    title = "Padel",
                    value = padelCount.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.secondaryContainer,
                    valueColor = MaterialTheme.colorScheme.onSecondaryContainer,
                )
            }
            Spacer(Modifier.height(8.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                OverviewCard(
                    title = "Offen",
                    value = openCount.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.errorContainer,
                    valueColor = MaterialTheme.colorScheme.onErrorContainer,
                )
                OverviewCard(
                    title = "E-Mail offen",
                    value = uiState.emailNotVerified.size.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.tertiaryContainer,
                    valueColor = MaterialTheme.colorScheme.onTertiaryContainer,
                )
                OverviewCard(
                    title = "Tennis",
                    value = tennisCount.toString(),
                    modifier = Modifier.weight(1f),
                    containerColor = MaterialTheme.colorScheme.surfaceVariant,
                    valueColor = MaterialTheme.colorScheme.onSurface,
                )
            }
        }

        uiState.error?.let {
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                Text(
                    text = it,
                    color = MaterialTheme.colorScheme.error,
                    modifier = Modifier.weight(1f),
                )
                OutlinedButton(onClick = { viewModel.refresh() }) { Text("Erneut laden") }
            }
        }

        panelMessage?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(top = 8.dp)
            )
        }

        deleteRowError?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.padding(top = 8.dp)
            )
        }

        if (uiState.isLoading) {
            Row(
                modifier = Modifier.padding(top = 12.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                CircularProgressIndicator()
                Text(text = "Lade Mitglieder...")
            }
            return@Column
        }

        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 12.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp)
        ) {
            if (showConfigTab) item {
                SectionContainer(title = "Zeitslot-Konfiguration") {
                    OutlinedTextField(
                        value = firstSlot,
                        onValueChange = { firstSlot = it },
                        label = { Text("Erster Slot (HH:MM)") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(Modifier.height(8.dp))
                    OutlinedTextField(
                        value = lastSlot,
                        onValueChange = { lastSlot = it },
                        label = { Text("Letzter Slot (HH:MM)") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(Modifier.height(8.dp))
                    OutlinedTextField(
                        value = intervalMinutes,
                        onValueChange = { intervalMinutes = it },
                        label = { Text("Intervall in Minuten") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }

            if (showConfigTab) item {
                SectionContainer(title = "Buchungsfenster") {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Buchungen aktivieren")
                        Switch(
                            checked = bookingEnabled,
                            onCheckedChange = { bookingEnabled = it }
                        )
                    }

                    Spacer(Modifier.height(8.dp))
                    OutlinedTextField(
                        value = bookingOpenAt,
                        onValueChange = { bookingOpenAt = it },
                        label = { Text("Oeffnet ab") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(Modifier.height(8.dp))
                    Button(onClick = { viewModel.refresh() }) {
                        Text("Konfiguration speichern")
                    }
                }
            }

            if (showConfigTab) item {
                SectionContainer(title = "AGB / Sichtbarkeit") {
                    Text(
                        text = "Wenn deaktiviert, sehen Mitglieder bei belegten Slots keine Namen mehr (nur \"Belegt\"). " +
                            "Admins und der Buchende selbst sehen weiterhin Details.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.height(8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Namen bei Buchungen anzeigen")
                        Switch(
                            checked = bookingNamesPublic,
                            onCheckedChange = {
                                bookingNamesPublic = it
                                bookingNamesFeedback = null
                            }
                        )
                    }

                    Spacer(Modifier.height(8.dp))
                    Button(
                        onClick = {
                            bookingNamesFeedback = null
                            scope.launch {
                                val result = configRepository.setBookingNamesPublic(bookingNamesPublic)
                                bookingNamesFeedback = result.fold(
                                    onSuccess = { "Gespeichert." },
                                    onFailure = { e -> e.message ?: "Speichern fehlgeschlagen." }
                                )
                            }
                        }
                    ) {
                        Text("Speichern")
                    }

                    bookingNamesFeedback?.let { msg ->
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = msg,
                            style = MaterialTheme.typography.bodySmall,
                            color = if (msg == "Gespeichert.") MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error
                        )
                    }
                }
            }

            if (showConfigTab) item {
                SectionContainer(title = "Mannschaften / Jahrgänge") {
                    OutlinedTextField(
                        value = newGroupName,
                        onValueChange = {
                            newGroupName = it
                            trainingGroupFeedback = null
                        },
                        label = { Text("Name") },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Spacer(Modifier.height(8.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        FilterChip(
                            selected = newGroupType == TrainingGroupType.TEAM,
                            onClick = { newGroupType = TrainingGroupType.TEAM },
                            label = { Text("Mannschaft") }
                        )
                        FilterChip(
                            selected = newGroupType == TrainingGroupType.YEAR,
                            onClick = { newGroupType = TrainingGroupType.YEAR },
                            label = { Text("Jahrgang") }
                        )
                    }
                    Spacer(Modifier.height(8.dp))
                    Button(onClick = {
                        scope.launch {
                            val result = userRepository.createTrainingGroup(newGroupName, newGroupType)
                            trainingGroupFeedback = result.fold(
                                onSuccess = {
                                    newGroupName = ""
                                    trainingGroups = userRepository.getTrainingGroups()
                                    "Gespeichert."
                                },
                                onFailure = { e -> e.message ?: "Speichern fehlgeschlagen." }
                            )
                        }
                    }) {
                        Text("Anlegen")
                    }

                    if (trainingGroups.isEmpty()) {
                        Spacer(Modifier.height(8.dp))
                        Text("Noch keine Gruppen vorhanden.", style = MaterialTheme.typography.bodySmall)
                    } else {
                        Spacer(Modifier.height(8.dp))
                        trainingGroups.forEach { group ->
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Text("${if (group.type == TrainingGroupType.TEAM) "Mannschaft" else "Jahrgang"}: ${group.name}")
                                OutlinedButton(onClick = {
                                    scope.launch {
                                        val result = userRepository.deleteTrainingGroup(group.id)
                                        trainingGroupFeedback = result.fold(
                                            onSuccess = {
                                                trainingGroups = userRepository.getTrainingGroups()
                                                "Gelöscht."
                                            },
                                            onFailure = { e -> e.message ?: "Löschen fehlgeschlagen." }
                                        )
                                    }
                                }) {
                                    Text("Entfernen")
                                }
                            }
                        }
                    }

                    trainingGroupFeedback?.let { msg ->
                        Spacer(Modifier.height(8.dp))
                        Text(msg, style = MaterialTheme.typography.bodySmall)
                    }
                }
            }

            if (showConfigTab) item {
                SectionContainer(title = "Wartung") {
                    Text(
                        text = "Füllt fehlende Namen/Datenschutz-Snapshots in alten Buchungen nach.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.height(8.dp))
                    Button(
                        enabled = !backfillLoading,
                        onClick = {
                            backfillFeedback = null
                            backfillLoading = true
                            scope.launch {
                                functionsRepository.adminBackfillBookingPlayerSnapshots(limit = 3000, dryRun = false)
                                    .onSuccess {
                                        backfillFeedback = "Backfill gestartet/abgeschlossen. Bitte Seite neu laden."
                                    }
                                    .onFailure { e ->
                                        backfillFeedback = e.message ?: "Backfill fehlgeschlagen."
                                    }
                                backfillLoading = false
                            }
                        }
                    ) {
                        if (backfillLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                            Spacer(Modifier.width(10.dp))
                            Text("Läuft...")
                        } else {
                            Text("Buchungs-Snapshots nachfüllen")
                        }
                    }

                    backfillFeedback?.let { msg ->
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = msg,
                            style = MaterialTheme.typography.bodySmall,
                            color = if (msg.contains("fehl", ignoreCase = true)) {
                                MaterialTheme.colorScheme.error
                            } else {
                                MaterialTheme.colorScheme.primary
                            }
                        )
                    }

                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = "Entfernt das Legacy-Feld 'status' aus allen Usern.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.height(8.dp))
                    OutlinedButton(
                        enabled = !statusCleanupLoading,
                        onClick = {
                            statusCleanupFeedback = null
                            statusCleanupLoading = true
                            scope.launch {
                                functionsRepository.adminRemoveLegacyUserStatusField()
                                    .onSuccess {
                                        statusCleanupFeedback = "Legacy-Status wurde entfernt."
                                        viewModel.refresh()
                                    }
                                    .onFailure { e ->
                                        statusCleanupFeedback = e.message ?: "Status-Bereinigung fehlgeschlagen."
                                    }
                                statusCleanupLoading = false
                            }
                        }
                    ) {
                        if (statusCleanupLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                            Spacer(Modifier.width(10.dp))
                            Text("Läuft...")
                        } else {
                            Text("Legacy-Status entfernen")
                        }
                    }

                    statusCleanupFeedback?.let { msg ->
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = msg,
                            style = MaterialTheme.typography.bodySmall,
                            color = if (msg.contains("fehl", ignoreCase = true)) {
                                MaterialTheme.colorScheme.error
                            } else {
                                MaterialTheme.colorScheme.primary
                            }
                        )
                    }

                    if (DebugRegistrationConfig.enabled) {
                        Spacer(Modifier.height(12.dp))
                        Text(
                            text = "Legt die beiden Debug-Test-Konten an bzw. setzt sie zurück (nur lokal sichtbar).",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Spacer(Modifier.height(8.dp))
                        OutlinedButton(
                            enabled = !uiState.testUsersResetLoading,
                            onClick = { viewModel.resetDebugTestUsers() }
                        ) {
                            if (uiState.testUsersResetLoading) {
                                CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                                Spacer(Modifier.width(10.dp))
                                Text("Läuft...")
                            } else {
                                Text("Test-Konten zurücksetzen")
                            }
                        }

                        uiState.testUsersResetFeedback?.let { msg ->
                            Spacer(Modifier.height(8.dp))
                            Text(
                                text = msg,
                                style = MaterialTheme.typography.bodySmall,
                                color = if (msg.contains("fehl", ignoreCase = true)) {
                                    MaterialTheme.colorScheme.error
                                } else {
                                    MaterialTheme.colorScheme.primary
                                }
                            )
                        }
                    }
                }
            }

            if (showFiltersTab) item {
                SectionContainer(title = "Filter & Sortierung") {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Registrierungsdatum: $sortLabel")
                        Button(onClick = {
                            sortLabel = if (sortLabel == "Neueste zuerst") "Aelteste zuerst" else "Neueste zuerst"
                        }) {
                            Text("Umschalten")
                        }
                    }

                    Spacer(Modifier.height(8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("In Kategorien nach Registrierungsdatum sortieren")
                        Switch(
                            checked = sortByRegistrationDate,
                            onCheckedChange = { sortByRegistrationDate = it }
                        )
                    }

                    Spacer(Modifier.height(8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Benutzer mit Mitgliedsnummer ausblenden")
                        Switch(
                            checked = hideMembersWithId,
                            onCheckedChange = { hideMembersWithId = it }
                        )
                    }

                    Spacer(Modifier.height(8.dp))

                    Text("Datenschutz-Zustimmung", fontWeight = FontWeight.SemiBold)
                    Spacer(Modifier.height(6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        FilterChip(
                            selected = privacyConsentFilter == PrivacyConsentFilter.ALL,
                            onClick = { privacyConsentFilter = PrivacyConsentFilter.ALL },
                            label = { Text("Alle") }
                        )
                        FilterChip(
                            selected = privacyConsentFilter == PrivacyConsentFilter.ACCEPTED,
                            onClick = { privacyConsentFilter = PrivacyConsentFilter.ACCEPTED },
                            label = { Text("Akzeptiert") }
                        )
                        FilterChip(
                            selected = privacyConsentFilter == PrivacyConsentFilter.NOT_ACCEPTED,
                            onClick = { privacyConsentFilter = PrivacyConsentFilter.NOT_ACCEPTED },
                            label = { Text("Offen") }
                        )
                    }

                    Spacer(Modifier.height(8.dp))

                    Text("Sparten-Filter", fontWeight = FontWeight.SemiBold)
                    Spacer(Modifier.height(6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        SPORTS.forEach { sport ->
                            val selected = selectedSports.contains(sport)
                            FilterChip(
                                selected = selected,
                                onClick = {
                                    selectedSports = if (selected) selectedSports - sport else selectedSports + sport
                                },
                                label = { Text(sport) }
                            )
                        }
                    }

                    Spacer(Modifier.height(8.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Text("Nur mit Mitgliedsnummer (wenn Sparten-Filter aktiv)")
                        Switch(
                            checked = onlyWithIdWhenSportFilter,
                            onCheckedChange = { onlyWithIdWhenSportFilter = it }
                        )
                    }

                    Spacer(Modifier.height(8.dp))
                    OutlinedTextField(
                        value = selectedDateFilter,
                        onValueChange = { selectedDateFilter = it },
                        label = { Text("Nur Nutzer ab bestimmtem Datum anzeigen") },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }

            if (showMembersTab) item(key = "members-title") {
                Text(
                    text = "Benutzerverwaltung",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold
                )
            }

            // Suche direkt über der Liste (vorher versteckt im Reiter "Filter").
            if (showMembersTab) item(key = "members-search") {
                OutlinedTextField(
                    value = searchQuery,
                    onValueChange = { searchQuery = it },
                    label = { Text("Name, E-Mail oder UID suchen") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            if (showMembersTab) item(key = "export-emails") {
                SectionContainer(title = "E-Mail-Export") {
                    Text(
                        text = "Exportiert nur E-Mail-Adressen von Admins, Trainern sowie Padel-/Tennis-Mitgliedern.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.height(8.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        Button(
                            onClick = {
                                exportFeedback = null

                                if (exportCsvText.isBlank()) {
                                    exportFeedback = "Keine E-Mail-Adressen zum Export gefunden."
                                    return@Button
                                }

                                val downloaded = exportCsv(
                                    filename = "emails.csv",
                                    csvText = exportCsvText
                                )
                                if (!downloaded) {
                                    clipboardManager.setText(AnnotatedString(exportCsvText))
                                }
                                exportFeedback = if (downloaded) {
                                    "CSV-Download wurde gestartet."
                                } else {
                                    "CSV wurde in die Zwischenablage kopiert."
                                }
                            }
                        ) {
                            Text("E-Mails exportieren")
                        }

                        OutlinedButton(
                            onClick = {
                                exportFeedback = null
                                if (exportCsvText.isBlank()) {
                                    exportFeedback = "Keine E-Mail-Adressen zum Kopieren gefunden."
                                    return@OutlinedButton
                                }
                                clipboardManager.setText(AnnotatedString(exportCsvText))
                                exportFeedback = "CSV wurde in die Zwischenablage kopiert."
                            }
                        ) {
                            Text("Kopieren")
                        }
                    }

                    exportFeedback?.let { msg ->
                        Spacer(Modifier.height(8.dp))
                        Text(
                            text = msg,
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            }

            if (showMembersTab) {
                memberGroupItems(
                    title = "Admins",
                    groupKey = "admins",
                    members = displayedAdmins,
                    onMemberClick = openEditor,
                )
                memberGroupItems(
                    title = "Trainer",
                    groupKey = "trainers",
                    members = displayedTrainers,
                    onMemberClick = openEditor,
                )
                memberGroupItems(
                    title = "Sparte: Padel",
                    groupKey = "sport-padel",
                    members = displayedPadelMembers,
                    onMemberClick = openEditor,
                )
                memberGroupItems(
                    title = "Sparte: Tennis",
                    groupKey = "sport-tennis",
                    members = displayedTennisMembers,
                    onMemberClick = openEditor,
                )
                memberGroupItems(
                    title = "E-Mail nicht bestaetigt",
                    groupKey = "email-not-verified",
                    members = displayedEmailNotVerified,
                    showResendButton = true,
                    showDeleteButton = true,
                    resendLoadingUids = uiState.resendLoadingUids,
                    resendSuccessUids = uiState.resendSuccessUids,
                    resendErrorByUid = uiState.resendErrorByUid,
                    onResendClick = viewModel::resendVerificationEmailForUser,
                    onDeleteClick = { uid ->
                        deleteRowError = null
                        deleteRowUid = uid
                    },
                )
                memberGroupItems(
                    title = "Mitglied ohne Sparte",
                    groupKey = "member-no-sport",
                    members = displayedMembersWithoutSport,
                    onMemberClick = openEditor,
                )
                memberGroupItems(
                    title = "Kein Mitglied",
                    groupKey = "no-sport",
                    members = displayedNoMemberMembers,
                    onMemberClick = openEditor,
                )
            }

            if (showOverviewTab) item(key = "overview-hint") {
                SectionContainer(title = "Schnellzugriff") {
                    Text(
                        text = "Wechsle zu 'Nutzer' fuer Verwaltung, 'Filter' fuer Sortierung und 'Konfig' fuer Einstellungen.",
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
        }
    }

    // Bestätigungsdialog: User löschen (aus Liste)
    if (deleteRowUid != null) {
        val uidToDelete = deleteRowUid!!
        androidx.compose.material3.AlertDialog(
            onDismissRequest = { if (!deleteRowLoading) deleteRowUid = null },
            title = { Text("Nutzer löschen") },
            text = {
                Text(
                    "Möchtest du den Nutzer wirklich löschen?\n\n" +
                        "UID: ${uidToDelete.take(12)}…"
                )
            },
            confirmButton = {
                Button(
                    enabled = !deleteRowLoading,
                    onClick = {
                        scope.launch {
                            deleteRowLoading = true
                            deleteRowError = null

                            val currentUid = Firebase.auth.currentUser?.uid
                            if (!currentUid.isNullOrBlank() && currentUid == uidToDelete) {
                                deleteRowError = "Du kannst dich nicht selbst löschen."
                                deleteRowLoading = false
                                return@launch
                            }

                            functionsRepository.adminDeleteUser(uidToDelete)
                                .onSuccess {
                                    viewModel.refresh()
                                    deleteRowUid = null
                                }
                                .onFailure { e ->
                                    deleteRowError = e.message ?: "Löschen fehlgeschlagen."
                                }

                            deleteRowLoading = false
                        }
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    if (deleteRowLoading) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                        Spacer(Modifier.widthIn(10.dp))
                        Text("Lösche…")
                    } else {
                        Text("Löschen")
                    }
                }
            },
            dismissButton = {
                OutlinedButton(
                    enabled = !deleteRowLoading,
                    onClick = { deleteRowUid = null }
                ) { Text("Abbrechen") }
            }
        )
    }
}

@Composable
fun AdminPanelPreviewContent(
    uiState: AdminPanelUiState,
    contentPadding: PaddingValues = PaddingValues(),
) {
    val allMembers = remember(uiState) {
        (uiState.admins + uiState.trainers + uiState.approved + uiState.pending + uiState.emailNotVerified)
            .distinctBy { it.uid }
    }
    val padelMembers = remember(allMembers) { allMembers.filter(::memberBelongsToPadelGroup) }
    val tennisMembers = remember(allMembers) { allMembers.filter(::memberBelongsToTennisGroup) }
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(contentPadding)
            .padding(16.dp)
    ) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(
                text = "Adminbereich",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.SemiBold
            )
            Text("Offen: ${uiState.emailNotVerified.size}")
        }

        uiState.error?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.padding(top = 12.dp)
            )
        }

        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 12.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp)
        ) {
            memberGroupItems(
                title = "Admins",
                groupKey = "preview-admins",
                members = uiState.admins,
            )
            memberGroupItems(
                title = "Trainer",
                groupKey = "preview-trainers",
                members = uiState.trainers,
            )
            memberGroupItems(
                title = "Sparte: Padel",
                groupKey = "preview-padel",
                members = padelMembers,
            )
            memberGroupItems(
                title = "Sparte: Tennis",
                groupKey = "preview-tennis",
                members = tennisMembers,
            )
            memberGroupItems(
                title = "E-Mail nicht bestaetigt",
                groupKey = "preview-email-not-verified",
                members = uiState.emailNotVerified,
            )
        }
    }
}

@Composable
private fun SectionContainer(
    title: String,
    content: @Composable () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.colorScheme.surface,
                shape = RoundedCornerShape(12.dp)
            )
            .border(
                width = 1.dp,
                color = MaterialTheme.colorScheme.outlineVariant,
                shape = RoundedCornerShape(12.dp)
            )
            .padding(12.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleMedium,
            fontWeight = FontWeight.SemiBold
        )
        Spacer(Modifier.height(10.dp))
        content()
    }
}

@Composable
private fun OverviewCard(
    title: String,
    value: String,
    modifier: Modifier = Modifier,
    containerColor: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.secondaryContainer,
    valueColor: androidx.compose.ui.graphics.Color = MaterialTheme.colorScheme.onSecondaryContainer,
) {
    Box(
        modifier = modifier
            .background(containerColor, RoundedCornerShape(12.dp))
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(12.dp))
            .padding(12.dp)
    ) {
        Column {
            Text(
                text = title,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Spacer(Modifier.height(6.dp))
            Text(
                text = value,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.SemiBold,
                color = valueColor
            )
        }
    }
}

private fun applySearch(members: List<MemberSummary>, query: String): List<MemberSummary> {
    val q = query.trim().lowercase()
    if (q.isBlank()) return members

    return members.filter { member ->
        member.displayName.lowercase().contains(q) ||
            member.email.lowercase().contains(q) ||
            member.uid.lowercase().contains(q)
    }
}

private fun applyPrivacyConsentFilter(
    members: List<MemberSummary>,
    filter: PrivacyConsentFilter,
): List<MemberSummary> {
    return when (filter) {
        PrivacyConsentFilter.ALL -> members
        PrivacyConsentFilter.ACCEPTED -> members.filter { it.privacyConsentAccepted }
        PrivacyConsentFilter.NOT_ACCEPTED -> members.filter { !it.privacyConsentAccepted }
    }
}

private fun applySportAndMemberNumberFilters(
    members: List<MemberSummary>,
    selectedSports: Set<String>,
    hideMembersWithId: Boolean,
    onlyWithIdWhenSportFilter: Boolean,
): List<MemberSummary> {
    val selectedSportKeys = selectedSports
        .map(::normalizeSportKey)
        .toSet()
    val selectedCanonicalSportKeys = selectedSportKeys - NO_SPORT_FILTER_KEY
    val includeMembersWithoutSport = NO_SPORT_FILTER_KEY in selectedSportKeys

    val sportFilterActive = selectedSportKeys.isNotEmpty()

    return members.filter { member ->
        val hasMemberNumber = member.memberNumber.trim().isNotBlank()
        if (hideMembersWithId && hasMemberNumber) return@filter false

        if (!sportFilterActive) return@filter true

        val memberSportKeys = member.sports
            .mapNotNull(::toCanonicalSportLabel)
            .map(::normalizeSportKey)
            .toSet()
        val hasAnySport = memberHasAnySport(member)

        val matchesSelectedSport =
            (includeMembersWithoutSport && !hasAnySport) ||
                memberSportKeys.any { it in selectedCanonicalSportKeys }
        if (!matchesSelectedSport) return@filter false

        if (onlyWithIdWhenSportFilter && !hasMemberNumber) return@filter false
        true
    }
}

private fun memberHasAnySport(member: MemberSummary): Boolean {
    return member.sports.any { it.trim().isNotBlank() }
}

private fun memberHasSport(member: MemberSummary, sportLabel: String): Boolean {
    val targetKey = normalizeSportKey(sportLabel)
    return member.sports
        .mapNotNull(::toCanonicalSportLabel)
        .map(::normalizeSportKey)
        .any { it == targetKey }
}

private fun memberBelongsToTennisGroup(member: MemberSummary): Boolean {
    return memberHasSport(member, "Tennis")
}

private fun memberBelongsToPadelGroup(member: MemberSummary): Boolean {
    return memberHasSport(member, "Padel") && !memberBelongsToTennisGroup(member)
}

private fun sortMembersForDisplay(
    members: List<MemberSummary>,
    sortByRegistrationDate: Boolean,
    newestFirst: Boolean,
): List<MemberSummary> {
    if (!sortByRegistrationDate) return members

    val base = compareBy<MemberSummary> { member ->
        member.createdAtEpochMillis == null || member.createdAtEpochMillis <= 0L
    }

    val byDate = if (newestFirst) {
        base.thenByDescending { it.createdAtEpochMillis ?: Long.MIN_VALUE }
    } else {
        base.thenBy { it.createdAtEpochMillis ?: Long.MAX_VALUE }
    }

    return members.sortedWith(byDate.thenBy { it.displayName.lowercase() })
}

private fun statusLabel(status: UserStatus): String {
    return when (status) {
        UserStatus.APPROVED -> "Padel/Tennis"
        UserStatus.PENDING -> "Kein Mitglied"
        UserStatus.EMAIL_NOT_VERIFIED -> "E-Mail offen"
    }
}

@OptIn(kotlin.time.ExperimentalTime::class)
private fun formatRegistrationDate(epochMillis: Long?): String {
    if (epochMillis == null || epochMillis <= 0L) return "unbekannt"
    val instant = runCatching { Instant.fromEpochMilliseconds(epochMillis) }
        .getOrElse { return epochMillis.toString() }

    val zone = runCatching { TimeZone.currentSystemDefault() }
        .getOrDefault(TimeZone.UTC)

    return runCatching {
        val dt = instant.toLocalDateTime(zone)
        val day = dt.dayOfMonth.toString().padStart(2, '0')
        val month = dt.monthNumber.toString().padStart(2, '0')
        val year = dt.year.toString()
        val hour = dt.hour.toString().padStart(2, '0')
        val minute = dt.minute.toString().padStart(2, '0')
        "$day.$month.$year $hour:$minute"
    }.getOrElse {
        instant.toString()
    }
}

private fun LazyListScope.memberGroupItems(
    title: String,
    groupKey: String,
    members: List<MemberSummary>,
    showResendButton: Boolean = false,
    showDeleteButton: Boolean = false,
    resendLoadingUids: Set<String> = emptySet(),
    resendSuccessUids: Set<String> = emptySet(),
    resendErrorByUid: Map<String, String> = emptyMap(),
    onResendClick: ((String) -> Unit)? = null,
    onDeleteClick: ((String) -> Unit)? = null,
    onMemberClick: ((MemberSummary) -> Unit)? = null,
) {
    item(key = "header-$groupKey") {
        Text(
            text = "$title (${members.size})",
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.padding(top = 4.dp)
        )
    }

    if (members.isEmpty()) {
        item(key = "empty-$groupKey") {
            Text(
                text = "Keine Eintrage",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    } else {
        item(key = "table-header-$groupKey") {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(8.dp))
                    .padding(horizontal = 10.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "Nutzer",
                    modifier = Modifier.weight(2f),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = "Status",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = "Registriert",
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = "UID",
                    modifier = Modifier.width(70.dp),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.SemiBold
                )
            }
        }

        items(
            items = members,
            key = { member -> "$groupKey:${member.uid}" },
            contentType = { "member" }
        ) { member ->
            MemberRow(
                member = member,
                showResendButton = showResendButton,
                showDeleteButton = showDeleteButton,
                isResending = resendLoadingUids.contains(member.uid),
                resendSuccess = resendSuccessUids.contains(member.uid),
                resendError = resendErrorByUid[member.uid],
                onResendClick = onResendClick,
                onDeleteClick = onDeleteClick,
                onClick = if (onMemberClick != null) ({ onMemberClick(member) }) else null,
            )
        }
    }

    item(key = "spacer-$groupKey") {
        Spacer(Modifier.height(10.dp))
    }
}

@Composable
private fun MemberRow(
    member: MemberSummary,
    showResendButton: Boolean = false,
    showDeleteButton: Boolean = false,
    isResending: Boolean = false,
    resendSuccess: Boolean = false,
    resendError: String? = null,
    onResendClick: ((String) -> Unit)? = null,
    onDeleteClick: ((String) -> Unit)? = null,
    onClick: (() -> Unit)? = null,
) {
    val emailText = member.email.ifBlank { "keine E-Mail" }
    val registrationText = formatRegistrationDate(member.createdAtEpochMillis)
    // Redesign Stufe 2: Mitglied als Karte mit Initialen, Name, E-Mail und Status-Plakette.
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(14.dp))
            .border(1.dp, MaterialTheme.colorScheme.outlineVariant, RoundedCornerShape(14.dp))
            .then(
                if (onClick != null) {
                    Modifier.clickable { onClick() }
                } else {
                    Modifier
                }
            )
            .padding(horizontal = 14.dp, vertical = 12.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .background(MaterialTheme.colorScheme.primaryContainer, CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = memberInitials(member.displayName),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onPrimaryContainer,
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = member.displayName,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = emailText,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    text = "Registriert: $registrationText",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            StatusPill(member.status)
        }

        if (showResendButton && onResendClick != null) {
            Spacer(Modifier.height(6.dp))
            if (isResending) {
                CircularProgressIndicator()
            } else {
                OutlinedButton(
                    onClick = { onResendClick(member.uid) }
                ) {
                    Text("Verifizierungs-Mail erneut senden")
                }
            }

            if (resendSuccess) {
                Text(
                    text = "Mail wurde gesendet.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.primary
                )
            }

            resendError?.let {
                Text(
                    text = it,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.error
                )
            }
        }

        if (showDeleteButton && onDeleteClick != null) {
            Spacer(Modifier.height(6.dp))
            OutlinedButton(
                onClick = { onDeleteClick(member.uid) },
                colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.error)
            ) {
                Text("Nutzer löschen")
            }
        }
    }
}

@Composable
private fun MemberEditDialog(
    profile: MemberProfile?,
    isLoading: Boolean,
    loadError: String?,
    isDeleting: Boolean,
    deleteError: String?,
    functionsRepository: FunctionsRepository,
    availableTrainingGroups: List<TrainingGroup>,
    autocompleteAddressUseCase: AutocompleteAddressUseCase,
    onDismiss: () -> Unit,
    onSave: (MemberProfile) -> Unit,
    onDelete: (String) -> Unit,
) {
    Dialog(onDismissRequest = onDismiss) {
        androidx.compose.material3.Surface(
            shape = RoundedCornerShape(16.dp),
            tonalElevation = 2.dp,
            modifier = Modifier
                .widthIn(min = 320.dp, max = 720.dp)
                .fillMaxHeight(0.9f)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = "Account bearbeiten",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.SemiBold
                    )
                    OutlinedButton(onClick = onDismiss) { Text("Schließen") }
                }

                Spacer(Modifier.height(8.dp))

                if (isLoading) {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                        Text("Lade Profil...")
                    }
                    return@Surface
                }

                loadError?.let {
                    Text(it, color = MaterialTheme.colorScheme.error)
                    Spacer(Modifier.height(8.dp))
                }

                if (profile == null) {
                    Text("Profil nicht verfügbar.", color = MaterialTheme.colorScheme.error)
                    return@Surface
                }

                MemberEditForm(
                    profile = profile,
                    functionsRepository = functionsRepository,
                    availableTrainingGroups = availableTrainingGroups,
                    autocompleteAddressUseCase = autocompleteAddressUseCase,
                    onSave = onSave,
                    onDelete = onDelete,
                    isDeleting = isDeleting,
                    deleteError = deleteError,
                )
            }
        }
    }
}

@Composable
private fun MemberEditForm(
    profile: MemberProfile,
    functionsRepository: FunctionsRepository,
    availableTrainingGroups: List<TrainingGroup>,
    autocompleteAddressUseCase: AutocompleteAddressUseCase,
    onSave: (MemberProfile) -> Unit,
    onDelete: (String) -> Unit,
    isDeleting: Boolean,
    deleteError: String?,
) {
    val scope = rememberCoroutineScope()
    var firstName by rememberSaveable(profile.uid) { mutableStateOf(profile.firstName) }
    var lastName by rememberSaveable(profile.uid) { mutableStateOf(profile.lastName) }
    var memberNumber by rememberSaveable(profile.uid) { mutableStateOf(profile.memberNumber) }
    var phone by rememberSaveable(profile.uid) { mutableStateOf(profile.phone) }
    var addressText by rememberSaveable(profile.uid) { mutableStateOf(profile.addressText) }
    var placeId by rememberSaveable(profile.uid) { mutableStateOf(profile.placeId.orEmpty()) }
    val initialSelectedSports = remember(profile.uid, profile.sports) {
        profile.sports
            .mapNotNull(::toCanonicalSportLabel)
            .toSet()
    }
    var selectedSports by remember(profile.uid, initialSelectedSports) { mutableStateOf(initialSelectedSports) }
    var isAdmin by rememberSaveable(profile.uid) { mutableStateOf(profile.isAdmin) }
    var isTrainer by rememberSaveable(profile.uid) { mutableStateOf(profile.isTrainer) }
    var manuallyChecked by rememberSaveable(profile.uid) { mutableStateOf(profile.manuallyChecked) }
    var trainerAssignments by remember(profile.uid) { mutableStateOf(profile.trainerAssignments.toSet()) }

    var showAddressPicker by rememberSaveable(profile.uid) { mutableStateOf(false) }

    var resetPwLoading by rememberSaveable(profile.uid) { mutableStateOf(false) }
    var resetPwSuccess by rememberSaveable(profile.uid) { mutableStateOf<String?>(null) }
    var resetPwError by rememberSaveable(profile.uid) { mutableStateOf<String?>(null) }
    var pendingNotifyTestLoading by rememberSaveable(profile.uid) { mutableStateOf(false) }
    var pendingNotifyTestFeedback by rememberSaveable(profile.uid) { mutableStateOf<String?>(null) }
    var pendingNotifyTestError by rememberSaveable(profile.uid) { mutableStateOf<String?>(null) }

    var showDeleteConfirm by rememberSaveable(profile.uid) { mutableStateOf(false) }

    Text(
        text = profile.email,
        style = MaterialTheme.typography.bodySmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
    Spacer(Modifier.height(10.dp))

    // Dialoge hier platzieren (außerhalb der LazyColumn-Content-Lambda), damit sie zuverlässig
    // in Composable-Context gerendert werden.
    if (showAddressPicker) {
        AddressPickerDialog(
            initialQuery = addressText,
            autocompleteAddressUseCase = autocompleteAddressUseCase,
            onDismiss = { showAddressPicker = false },
            onSelected = { suggestion ->
                addressText = suggestion.fullText
                placeId = suggestion.placeId
                showAddressPicker = false
            }
        )
    }

    if (showDeleteConfirm) {
        androidx.compose.material3.AlertDialog(
            onDismissRequest = { showDeleteConfirm = false },
            title = { Text("Nutzer löschen") },
            text = {
                Text(
                    "Möchtest du den Nutzer wirklich löschen?\n\n" +
                        "E-Mail: ${profile.email}\nUID: ${profile.uid.take(12)}…"
                )
            },
            confirmButton = {
                Button(
                    enabled = !isDeleting,
                    onClick = {
                        showDeleteConfirm = false
                        onDelete(profile.uid)
                    },
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                ) {
                    Text("Löschen")
                }
            },
            dismissButton = {
                OutlinedButton(
                    enabled = !isDeleting,
                    onClick = { showDeleteConfirm = false }
                ) {
                    Text("Abbrechen")
                }
            }
        )
    }

    LazyColumn(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        item {
            OutlinedTextField(
                value = firstName,
                onValueChange = { firstName = it },
                label = { Text("Vorname") },
                modifier = Modifier.fillMaxWidth()
            )
        }
        item {
            OutlinedTextField(
                value = lastName,
                onValueChange = { lastName = it },
                label = { Text("Nachname") },
                modifier = Modifier.fillMaxWidth()
            )
        }
        item {
            OutlinedTextField(
                value = memberNumber,
                onValueChange = { memberNumber = it },
                label = { Text("Mitgliedsnummer") },
                modifier = Modifier.fillMaxWidth()
            )
        }
        item {
            OutlinedTextField(
                value = phone,
                onValueChange = { phone = it },
                label = { Text("Handynummer") },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                modifier = Modifier.fillMaxWidth()
            )
        }
        item {
            Text("Adresse", fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(6.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                Text(
                    text = addressText.ifBlank { "Keine Adresse" },
                    modifier = Modifier.weight(1f),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                OutlinedButton(onClick = { showAddressPicker = true }) {
                    Text("Adresse wählen")
                }

                OutlinedButton(
                    onClick = {
                        addressText = ""
                        placeId = ""
                    }
                ) {
                    Text("X")
                }
            }
        }
        item {
            Text("Sparten", fontWeight = FontWeight.SemiBold)
            Spacer(Modifier.height(6.dp))
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                SPORTS_FOR_EDIT.forEach { sport ->
                    val selected = selectedSports.contains(sport)
                    FilterChip(
                        selected = selected,
                        onClick = {
                            selectedSports = if (selected) selectedSports - sport else selectedSports + sport
                        },
                        label = { Text(sport) }
                    )
                }
            }
        }
        item {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text("Admin")
                Switch(
                    checked = isAdmin,
                    onCheckedChange = { isAdmin = it }
                )
            }
        }
        item {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text("Trainer")
                Switch(
                    checked = isTrainer,
                    onCheckedChange = { isTrainer = it }
                )
            }
        }
        item {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text("Manuell überprüft")
                Switch(
                    checked = manuallyChecked,
                    onCheckedChange = { manuallyChecked = it }
                )
            }
        }

        if (isTrainer) {
            item {
                Text("Mannschaften/Jahrgänge", fontWeight = FontWeight.SemiBold)
                Spacer(Modifier.height(6.dp))
                if (availableTrainingGroups.isEmpty()) {
                    Text(
                        "Keine Gruppen vorhanden. Bitte zuerst oben im Adminbereich anlegen.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        availableTrainingGroups.forEach { group ->
                            val selected = trainerAssignments.contains(group.id)
                            val prefix = if (group.type == TrainingGroupType.TEAM) "M" else "J"
                            FilterChip(
                                selected = selected,
                                onClick = {
                                    trainerAssignments = if (selected) {
                                        trainerAssignments - group.id
                                    } else {
                                        trainerAssignments + group.id
                                    }
                                },
                                label = { Text("$prefix: ${group.name}") }
                            )
                        }
                    }
                }
            }
        }
        item {
            deleteError?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.error)
                Spacer(Modifier.height(6.dp))
            }

            resetPwSuccess?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.height(6.dp))
            }
            resetPwError?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.error)
                Spacer(Modifier.height(6.dp))
            }

            pendingNotifyTestFeedback?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                Spacer(Modifier.height(6.dp))
            }
            pendingNotifyTestError?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.error)
                Spacer(Modifier.height(6.dp))
            }

            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                OutlinedButton(
                    enabled = !resetPwLoading && profile.email.isNotBlank(),
                    onClick = {
                        resetPwSuccess = null
                        resetPwError = null
                        resetPwLoading = true
                        scope.launch {
                            runCatching {
                                Firebase.auth.sendPasswordResetEmail(profile.email)
                            }.onSuccess {
                                resetPwSuccess = "Passwort-Reset E-Mail wurde gesendet."
                            }.onFailure { e ->
                                resetPwError = e.message ?: "Passwort-Reset fehlgeschlagen."
                            }
                            resetPwLoading = false
                        }
                    }
                ) {
                    if (resetPwLoading) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                        Spacer(Modifier.widthIn(10.dp))
                        Text("Sende...")
                    } else {
                        Text("Passwort zurücksetzen")
                    }
                }

                Button(
                    onClick = {
                        onSave(
                            profile.copy(
                                firstName = firstName.trim(),
                                lastName = lastName.trim(),
                                memberNumber = memberNumber.trim(),
                                phone = phone.trim(),
                                addressText = addressText.trim(),
                                placeId = placeId.trim().ifBlank { null },
                                sports = selectedSports.toList(),
                                isAdmin = isAdmin,
                                isTrainer = isTrainer,
                                trainerAssignments = trainerAssignments.toList(),
                                manuallyChecked = manuallyChecked,
                            )
                        )
                    }
                ) {
                    Text("Speichern")
                }
            }

            Spacer(Modifier.height(8.dp))

            OutlinedButton(
                enabled = !pendingNotifyTestLoading,
                onClick = {
                    pendingNotifyTestFeedback = null
                    pendingNotifyTestError = null
                    pendingNotifyTestLoading = true
                    scope.launch {
                        functionsRepository.adminTriggerPendingAdminsNotification(profile.uid)
                            .onSuccess {
                                pendingNotifyTestFeedback = "Test-Trigger gesendet."
                            }
                            .onFailure { e ->
                                pendingNotifyTestError = e.message ?: "Trigger fehlgeschlagen."
                            }
                        pendingNotifyTestLoading = false
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                if (pendingNotifyTestLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    Spacer(Modifier.widthIn(10.dp))
                    Text("Trigger läuft...")
                } else {
                    Text("Pending-Mail testen")
                }
            }

            Spacer(Modifier.height(10.dp))

            val currentUid = Firebase.auth.currentUser?.uid
            val canDelete = currentUid.isNullOrBlank() || currentUid != profile.uid

            OutlinedButton(
                enabled = canDelete && !isDeleting,
                onClick = { showDeleteConfirm = true },
                colors = ButtonDefaults.outlinedButtonColors(
                    contentColor = MaterialTheme.colorScheme.error
                ),
                modifier = Modifier.fillMaxWidth()
            ) {
                if (isDeleting) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    Spacer(Modifier.widthIn(10.dp))
                    Text("Lösche…")
                } else {
                    Text("Nutzer löschen")
                }
            }

            if (!canDelete) {
                Spacer(Modifier.height(6.dp))
                Text(
                    text = "Du kannst dich nicht selbst löschen.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }

}

@Composable
private fun AddressPickerDialog(
    initialQuery: String,
    autocompleteAddressUseCase: AutocompleteAddressUseCase,
    onDismiss: () -> Unit,
    onSelected: (AddressSuggestion) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var query by rememberSaveable { mutableStateOf(initialQuery) }
    var suggestions by remember { mutableStateOf<List<AddressSuggestion>>(emptyList()) }
    var isSearching by remember { mutableStateOf(false) }
    var job by remember { mutableStateOf<Job?>(null) }

    Dialog(onDismissRequest = onDismiss) {
        androidx.compose.material3.Surface(
            shape = RoundedCornerShape(16.dp),
            tonalElevation = 2.dp,
            modifier = Modifier.widthIn(min = 320.dp, max = 720.dp)
        ) {
            Column(modifier = Modifier.padding(16.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text("Adresse wählen", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
                    OutlinedButton(onClick = onDismiss) { Text("Schließen") }
                }
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = query,
                    onValueChange = { value ->
                        query = value
                        job?.cancel()
                        if (value.trim().length < 3) {
                            suggestions = emptyList()
                            return@OutlinedTextField
                        }
                        job = scope.launch {
                            delay(300)
                            isSearching = true
                            suggestions = runCatching { autocompleteAddressUseCase(value.trim()) }
                                .getOrElse { emptyList() }
                            isSearching = false
                        }
                    },
                    label = { Text("Straße, Hausnummer, Ort") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Spacer(Modifier.height(8.dp))

                if (isSearching) {
                    Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                        CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                        Text("Suche...")
                    }
                    Spacer(Modifier.height(8.dp))
                }

                if (suggestions.isEmpty()) {
                    Text(
                        text = "Keine Vorschläge.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                } else {
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        items(
                            items = suggestions.take(8),
                            key = { it.placeId }
                        ) { s ->
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable { onSelected(s) }
                                    .padding(vertical = 8.dp)
                            ) {
                                Text(s.fullText)
                            }
                        }
                    }
                }
            }
        }
    }
}

/** Initialen für die Mitgliedskarte: "Anna Becker" -> "AB". */
internal fun memberInitials(displayName: String): String =
    displayName.split(' ', '-').filter { it.isNotBlank() }.take(2).joinToString("") { it.first().uppercase() }.ifEmpty { "?" }

@Composable
private fun StatusPill(status: UserStatus) {
    val (bg, fg) = when (status) {
        UserStatus.APPROVED -> MaterialTheme.colorScheme.primaryContainer to MaterialTheme.colorScheme.onPrimaryContainer
        UserStatus.PENDING -> MaterialTheme.colorScheme.secondaryContainer to MaterialTheme.colorScheme.onSecondaryContainer
        UserStatus.EMAIL_NOT_VERIFIED -> MaterialTheme.colorScheme.surfaceVariant to MaterialTheme.colorScheme.onSurfaceVariant
    }
    Text(
        text = statusLabel(status),
        style = MaterialTheme.typography.labelMedium,
        color = fg,
        modifier = Modifier
            .background(bg, RoundedCornerShape(50))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}
